import { Box, Collapse, DrawerBody, DrawerHeader, Flex, FormLabel, Heading } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { DrawerFixedButton } from 'components/Drawer';
import { FormikDatepicker, FormikInput, FormikSelectAsyncPaginate } from 'components/Formik';
import EmployeeList from 'components/List/EmployeeList';
import RadioInput from 'components/RadioCard';
import { timeOffDurationOptions } from 'containers/TimeOff/shared/options';
import { MONTH_DAY_YEAR_DASH_FORMAT } from 'global/appConstants';
import { roles } from 'global/rolesAndPermissions';
import { fetchPaginatedCategory } from 'hooks/timeOff/useFetchPaginatedCategories';
import { useSaveTimeOffRequest } from 'hooks/timeOff/useSaveTimeoffRequest';
import { useCompanyInfo, useUserInfo } from 'store/useUserStore';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import { initialRequestData, requestFormValidationSchema } from './schema';

interface IRequestTimeOff {
  onClose: () => void;
  formData?: typeof initialRequestData;
}

const RequestTimeOff: React.FC<IRequestTimeOff> = ({ onClose, formData = initialRequestData }) => {
  const { t } = useTranslation(['common', 'timeOff']);

  const userInfo = useUserInfo();
  const companyInfo = useCompanyInfo();

  const { status: saveRequestStatus, mutateAsync: mutateAsyncRequest } = useSaveTimeOffRequest();

  const loadCategoryOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedCategory, {
      page,
      search,
      active: '1',
    });
  };

  const handleCategoryChange = (
    selectedOption: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    values: typeof initialRequestData
  ) => {
    if (selectedOption.in_hours) {
      setFieldValue('time_off_duration_options', timeOffDurationOptions);
    } else {
      setFieldValue(
        'time_off_duration_options',
        timeOffDurationOptions.filter(option => option.value === 'Full Day')
      );
      setFieldValue('requested_hours_type', 'Full Day');
    }

    if (roles.ADMIN === userInfo?.roleId && selectedOption?.users.length > 0) {
      const selectedUserIds = new Set(values.user_ids.map((u: any) => u.userId || u.id));

      const { allEmployees, selectedEmployees } = (selectedOption?.users || []).reduce(
        (acc: { allEmployees: any[]; selectedEmployees: any[] }, user: any) => {
          const userId = user.id || user.userId;
          const mappedUser = {
            userId,
            firstName: user.firstName,
            lastName: user.lastName,
          };

          acc.allEmployees.push(mappedUser);

          if (selectedUserIds.has(userId)) {
            acc.selectedEmployees.push(mappedUser);
          }

          return acc;
        },
        { allEmployees: [], selectedEmployees: [] }
      );

      setFieldValue('employees', allEmployees);
      setFieldValue('user_ids', selectedEmployees);
    } else {
      setFieldValue('employees', []);
    }
  };

  const handleFormSubmit = async (values: typeof initialRequestData) => {
    const pFormData = {
      user_ids:
        userInfo?.roleId === roles.ADMIN ? values?.user_ids.map((user: any) => user.userId) : [userInfo?.userId],
      category_id: values?.category.id,
      start: values.start_date ? format(values.start_date, MONTH_DAY_YEAR_DASH_FORMAT) : undefined,
      end: values.end_date ? format(values.end_date, MONTH_DAY_YEAR_DASH_FORMAT) : undefined,
      requested_hours_type: values?.requested_hours_type || 'Full Day',
      description: values?.reason,
      ...(values.requested_hours_type === 'Specific Hours' ? { requested_hours: values?.requested_hours } : {}),
    };
    mutateAsyncRequest(pFormData).then(() => {
      if (onClose) {
        onClose();
      }
    });
  };

  return (
    <Box overflowY="auto">
      <DrawerHeader>{t('timeOff:requestTimeOff')}</DrawerHeader>
      <DrawerBody paddingBottom="100px">
        <Formik initialValues={formData} validationSchema={requestFormValidationSchema} onSubmit={handleFormSubmit}>
          {({ values, setFieldValue }) => (
            <Form noValidate autoComplete="off">
              <FormikSelectAsyncPaginate
                name="category"
                label={t('timeOff:timeOffCategory')}
                loadOptions={loadCategoryOptions}
                formControlProps={{ isRequired: true }}
                onChangeCallback={selectedOption => handleCategoryChange(selectedOption, setFieldValue, values)}
                selectProps={{
                  placeholder: t('timeOff:selectCategory'),
                  isMulti: false,
                  isClearable: false,
                  getOptionLabel: (option: any) => option.name,
                  getOptionValue: (option: any) => option.id,
                }}
              />
              {userInfo?.roleId === roles.ADMIN && (
                <Box mb={8}>
                  <Heading as="h4" size="sm" fontWeight="semibold" mb="3">
                    {t('common:selectUsers')}
                  </Heading>
                  <EmployeeList boxHeight="20vh" employees={values.employees} />
                </Box>
              )}

              <Flex justifyContent="space-between">
                <Box mr={3}>
                  <FormikDatepicker
                    label={t('timeOff:timeOffStart')}
                    name="start_date"
                    datepickerProps={{
                      dateFormat: companyInfo?.companyDateFormat,
                      startDate: (values.start_date as Date) || null,
                      endDate: (values.end_date as Date) || null,
                      popperPlacement: 'bottom-start',
                    }}
                    formControlProps={{
                      isRequired: true,
                    }}
                  />
                </Box>
                <Box ml={3}>
                  <FormikDatepicker
                    label={t('timeOff:timeOffEnd')}
                    name="end_date"
                    datepickerProps={{
                      startDate: (values.start_date as Date) || null,
                      endDate: (values.end_date as Date) || null,
                      minDate: (values.start_date as Date) || null,
                      dateFormat: companyInfo?.companyDateFormat,
                      popperPlacement: 'bottom-end',
                    }}
                    formControlProps={{
                      isRequired: true,
                    }}
                  />
                </Box>
              </Flex>
              <Box mb={6}>
                <FormLabel fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight={600}>
                  {t('timeOff:timeOffDuration')} (Per Day)
                </FormLabel>
                <RadioInput
                  variant="secondary"
                  options={values.time_off_duration_options}
                  name="requested_hours_type"
                  defaultValue={values.requested_hours_type}
                  handleChange={(e: any) => {
                    setFieldValue('requested_hours_type', e);
                  }}
                />
              </Box>

              <Collapse in={values.requested_hours_type === 'Specific Hours'} unmountOnExit={true} animateOpacity>
                <FormikInput
                  type="number"
                  name="requested_hours"
                  label={t('timeOff:timeoffDuration(InHrs)')}
                  formControlProps={{
                    isRequired: true,
                  }}
                />
              </Collapse>

              <FormikInput
                type="text"
                name="reason"
                label={t('common:reason')}
                formControlProps={{
                  isRequired: true,
                }}
              />
              <DrawerFixedButton>
                <Button
                  colorScheme="secondary"
                  type="submit"
                  width="100%"
                  size="lg"
                  isLoading={saveRequestStatus === 'loading'}
                >
                  {t('timeOff:submitTimeOff')}
                </Button>
              </DrawerFixedButton>
            </Form>
          )}
        </Formik>
      </DrawerBody>
    </Box>
  );
};
export default RequestTimeOff;
